import { useCallback, useEffect, useRef, useState } from 'react';
import { SessionHistory, type HistoryEntry, type PendingChange } from './sessionHistory.js';
import { overrideClassFor, type OverrideProp, type OverrideViewport } from '@evo-hub/shared';
/**
 * Clase de la utility de viñetas. Tiene que ser la MISMA que pone el servidor al
 * reconstruir el texto (LIST_UTILITY en manual-edit.ts): si difirieran, la lista
 * se vería de una forma antes de guardar y de otra después.
 */
const LIST_UTILITY = 'list-bullets';
import {
  duplicateElement, removeElement, restoreElement, moveElement, convertTag, canMove,
} from './structureOps.js';
import {
  classifyEditability,
  probeOf,
  editableValueOf,
  type Editability,
} from './textEditLedger.js';

// ── Edición de texto en el lugar, sobre el elemento de la vista previa ────────
//
// El usuario escribe directo sobre la página. Nada de esto pasa por el modelo.
//
// Lo que se acumula NO es HTML: es una lista de operaciones { targetId, value }.
// El servidor las aplica sobre el archivo que ya tiene. Serializar el DOM del
// iframe y mandarlo sería otra cosa: el navegador normaliza atributos y cierra
// tags, así que cada guardado reescribiría la página entera — justo lo que la
// edición quirúrgica vino a evitar.
//
// contenteditable ensucia el DOM mientras se edita (agrega el atributo, y pegar
// mete markup). No llega al archivo, porque el archivo no sale de acá: igual se
// limpia el atributo al terminar y el pegado se fuerza a texto plano, para que
// lo que se ve sea lo que se guarda.
//
// La lógica de qué cuenta como cambio vive en textEditLedger.ts, sin DOM.

export type { PendingChange };

export interface InlineTextEditApi {
  /** Cambios pendientes, para render. Puede ir un tick atrás del registro. */
  edits: PendingChange[];
  /**
   * Cambios pendientes, sincrónico.
   *
   * Usar SIEMPRE esto al guardar: `edits` es estado de React, así que dentro de
   * un handler que acaba de llamar a commitEdit() todavía tiene el valor del
   * render anterior, y el último elemento editado se perdería del guardado.
   */
  getEdits: () => PendingChange[];
  /** Deshacer / rehacer el último paso de la sesión. */
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  /** Revierte un cambio pendiente puntual, sin tocar los demás. */
  revert: (cambio: PendingChange) => void;
  /** Registra un cambio de clases ya aplicado al DOM, para que entre al historial. */
  registrarClases: (targetId: string, add: string[], remove: string[]) => void;
  registrarEstilo: (
    targetId: string,
    viewport: OverrideViewport,
    prop: OverrideProp,
    before: string | null,
    after: string | null,
  ) => void;
  /** Empieza a editar el elemento. Devuelve false si no es editable. */
  /**
   * Empieza a editar. `placeCaret` en false deja el cursor donde el navegador lo
   * puso: es lo que permite que caiga donde se hizo clic en vez de al final.
   */
  beginEdit: (el: HTMLElement, opts?: { placeCaret?: boolean }) => boolean;
  /** Termina la edición en curso y registra el cambio si lo hubo. */
  commitEdit: () => void;
  /** Descarta todo y restaura los textos originales en la vista. */
  discardAll: () => void;
  /** Olvida los cambios sin restaurar (para después de guardar). */
  clearEdits: () => void;
  /** Cómo se puede editar este elemento, o por qué no. */
  classify: (el: HTMLElement) => Editability;
  /**
   * Aplica formato sobre lo que se está editando. Solo tiene efecto en una hoja
   * (contenteditable completo); en un elemento con hijos no hay formato posible.
   */
  applyFormat: (accion: 'bold' | 'lineBreak' | 'bullets') => void;
  /** ¿Hay una edición abierta que acepta formato? */
  canFormat: boolean;
  /** El elemento que se está editando ahora, o null. Para render. */
  editingElement: HTMLElement | null;
  /**
   * Lo mismo, pero sincrónico.
   *
   * El estado de React se actualiza un render después. Entre el mousedown y el
   * mouseup de un arrastre no llega a propagarse, y quien decide si suprimir el
   * evento lee un valor viejo: el mouseup se cancelaba y con él la selección.
   */
  editingElementRef: React.MutableRefObject<HTMLElement | null>;
  /**
   * Qué acciones tienen sentido en lo que se está editando. Una lista adentro de
   * un título no es una lista: es markup roto con forma de lista.
   */
  formatActions: Array<'bold' | 'lineBreak' | 'bullets'>;

  // ── Lo que no es contenido del texto ──────────────────────────────────────
  /** Cambia el destino de un enlace. El valor ya validado lo decide el panel. */
  setHref: (targetId: string, value: string) => void;
  /** Convierte párrafo ↔ encabezado. Devuelve el elemento nuevo. */
  setTag: (targetId: string, value: string) => HTMLElement | null;
  /** Duplica un bloque justo debajo. Devuelve el id de la copia. */
  duplicateBlock: (targetId: string) => string | null;
  /** Borra un bloque. Se puede deshacer. */
  removeBlock: (targetId: string) => boolean;
  /** Sube o baja un bloque entre sus hermanos. */
  moveBlock: (targetId: string, direction: 'up' | 'down') => boolean;
  /** ¿Hay hermano hacia ese lado? Para no ofrecer un botón que no hace nada. */
  puedeMover: (targetId: string, direction: 'up' | 'down') => boolean;
}

/**
 * @param getDoc devuelve el documento del iframe. Se pasa como función y no como
 * valor porque el documento cambia cada vez que el iframe recarga.
 */
export function useInlineTextEdit(getDoc: () => Document | null | undefined): InlineTextEditApi {
  const historia = useRef(new SessionHistory());
  const [edits, setEdits] = useState<PendingChange[]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  /** Contenido de cada elemento tal como está AHORA, para armar el `before`. */
  const valorActual = useRef(new Map<string, string>());
  const editing = useRef<{
    el: HTMLElement;
    targetId: string;
    kind: 'rich' | 'own';
    /** El listener de input de ESTE elemento, para poder quitarlo después. */
    onInput: () => void;
    onSelectionChange: () => void;
  } | null>(null);

  // commitEdit se define más abajo; las operaciones de estructura que están más
  // arriba necesitan cerrar la edición antes de tocar el nodo.
  const commitEditRef = useRef<(() => void) | null>(null);

  const sync = useCallback((): void => {
    setEdits(historia.current.pending());
    setCanUndo(historia.current.canUndo);
    setCanRedo(historia.current.canRedo);
  }, []);
  const getEdits = useCallback((): PendingChange[] => historia.current.pending(), []);

  const classify = useCallback((el: HTMLElement): Editability => classifyEditability(probeOf(el)), []);

  /** Registra un paso de texto, si de verdad cambió algo. */
  const registrarTexto = useCallback((targetId: string, kind: 'rich' | 'own', after: string): void => {
    const before = valorActual.current.get(targetId) ?? after;
    if (before === after) return;
    historia.current.push({ type: 'text', targetId, kind, before, after }, Date.now());
    valorActual.current.set(targetId, after);
  }, []);

  /**
   * Registra un ajuste de estilo por vista.
   *
   * No toca el DOM: la hoja de preview se regenera desde los cambios
   * pendientes, así deshacer y rehacer funcionan sin un camino aparte.
   */
  const registrarEstilo = useCallback((
    targetId: string,
    viewport: OverrideViewport,
    prop: OverrideProp,
    before: string | null,
    after: string | null,
  ): void => {
    if (before === after) return;
    const el = getDoc()?.querySelector(`[data-evo-id="${targetId}"]`);
    if (after !== null) (el as HTMLElement | null)?.classList.add(overrideClassFor(targetId));
    historia.current.push({ type: 'style', targetId, viewport, prop, before, after }, Date.now());
    sync();
  }, [getDoc, sync]);

  /** Registra un cambio de clases ya aplicado sobre el DOM. */
  const registrarClases = useCallback((targetId: string, add: string[], remove: string[]): void => {
    historia.current.push({ type: 'class', targetId, add, remove }, Date.now());
    sync();
  }, [sync]);

  /** Cambia el destino de un enlace y lo registra. */
  const setHref = useCallback((targetId: string, after: string): void => {
    const el = getDoc()?.querySelector(`[data-evo-id="${targetId}"]`);
    if (!el) return;
    const before = el.getAttribute('href') ?? '';
    if (before === after) return;
    el.setAttribute('href', after);
    historia.current.push({ type: 'href', targetId, before, after }, Date.now());
    sync();
  }, [getDoc, sync]);

  /** Convierte párrafo ↔ encabezado y lo registra. */
  const setTag = useCallback((targetId: string, after: string): HTMLElement | null => {
    const el = getDoc()?.querySelector(`[data-evo-id="${targetId}"]`);
    // Nada de `instanceof` acá: el elemento vive en el documento del iframe, que
    // tiene su propia jerarquía de clases, y comparado contra el de esta ventana
    // da false SIEMPRE. Se comprueba por forma.
    const antes = (el as HTMLElement | null)?.tagName;
    if (typeof antes !== 'string') return null;
    const before = antes.toLowerCase();
    if (before === after) return null;
    // Reemplazar el nodo mata la edición abierta sobre él; se cierra antes para
    // que el registro del texto no se pierda.
    if (editing.current?.targetId === targetId) commitEditRef.current?.();
    const actual = getDoc()?.querySelector(`[data-evo-id="${targetId}"]`);
    const nuevo = actual ? convertTag(actual as HTMLElement, after) : null;
    if (!nuevo) return null;
    historia.current.push({ type: 'tag', targetId, before, after }, Date.now());
    sync();
    return nuevo;
  }, [getDoc, sync]);

  /** Duplica un bloque. Devuelve el id de la copia. */
  const duplicateBlock = useCallback((targetId: string): string | null => {
    const el = getDoc()?.querySelector(`[data-evo-id="${targetId}"]`);
    if (!el) return null;
    if (editing.current) commitEditRef.current?.();
    const r = duplicateElement(el as HTMLElement);
    if (!r) return null;
    historia.current.push({ type: 'structure', targetId, action: 'duplicate', idMap: r.idMap }, Date.now());
    sync();
    return r.idMap[targetId] ?? null;
  }, [getDoc, sync]);

  /** Borra un bloque, guardando lo necesario para poder deshacerlo. */
  const removeBlock = useCallback((targetId: string): boolean => {
    const el = getDoc()?.querySelector(`[data-evo-id="${targetId}"]`);
    if (!el) return false;
    if (editing.current) commitEditRef.current?.();
    const actual = getDoc()?.querySelector(`[data-evo-id="${targetId}"]`);
    const snap = actual ? removeElement(actual as HTMLElement) : null;
    if (!snap) return false;
    historia.current.push({
      type: 'structure', targetId, action: 'remove',
      html: snap.html, parentId: snap.parentId, nextId: snap.nextId,
    }, Date.now());
    sync();
    return true;
  }, [getDoc, sync]);

  /** Sube o baja un bloque entre sus hermanos. */
  const moveBlock = useCallback((targetId: string, direction: 'up' | 'down'): boolean => {
    const el = getDoc()?.querySelector(`[data-evo-id="${targetId}"]`);
    if (!el) return false;
    if (!moveElement(el as HTMLElement, direction)) return false;
    historia.current.push({ type: 'structure', targetId, action: 'move', direction }, Date.now());
    sync();
    return true;
  }, [getDoc, sync]);

  /** ¿Se puede mover hacia ese lado? Lo usa el panel para no ofrecer un botón muerto. */
  const puedeMover = useCallback((targetId: string, direction: 'up' | 'down'): boolean => {
    const el = getDoc()?.querySelector(`[data-evo-id="${targetId}"]`);
    return el ? canMove(el as HTMLElement, direction) : false;
  }, [getDoc]);

  /** Aplica un paso sobre el documento, en un sentido o en el inverso. */
  const aplicar = useCallback((e: HistoryEntry, invertir: boolean): void => {
    const doc = getDoc();
    if (!doc) return;

    // La estructura va ANTES de buscar el elemento: deshacer un borrado tiene que
    // funcionar justamente cuando el elemento ya no está en el documento.
    if (e.type === 'structure') {
      const buscar = (id: string): HTMLElement | null =>
        doc.querySelector(`[data-evo-id="${id}"]`);

      if (e.action === 'duplicate') {
        const copiaId = e.idMap?.[e.targetId];
        if (!copiaId) return;
        if (invertir) { buscar(copiaId)?.remove(); return; }
        // Rehacer: se vuelve a copiar el original y se le ponen los MISMOS ids de
        // la primera vez, para que un cambio pendiente sobre la copia siga
        // apuntando a algo.
        const original = buscar(e.targetId);
        if (!original) return;
        const clone = original.cloneNode(true) as HTMLElement;
        clone.removeAttribute('contenteditable');
        for (const n of [clone, ...Array.from(clone.querySelectorAll('[data-evo-id]'))]) {
          const viejo = n.getAttribute('data-evo-id');
          const nuevo = viejo ? e.idMap?.[viejo] : undefined;
          if (nuevo) n.setAttribute('data-evo-id', nuevo);
          n.removeAttribute('contenteditable');
        }
        original.after(clone);
        return;
      }

      if (e.action === 'remove') {
        if (invertir) {
          if (e.html !== undefined) {
            restoreElement(doc, { html: e.html, parentId: e.parentId ?? null, nextId: e.nextId ?? null });
          }
          return;
        }
        buscar(e.targetId)?.remove();
        return;
      }

      const el = buscar(e.targetId);
      if (!el || !e.direction) return;
      const hacia = invertir
        ? (e.direction === 'up' ? 'down' : 'up')
        : e.direction;
      moveElement(el, hacia);
      return;
    }

    const el = doc.querySelector(`[data-evo-id="${e.targetId}"]`);
    if (!el) return;

    if (e.type === 'href') {
      (el as HTMLAnchorElement).setAttribute('href', invertir ? e.before : e.after);
      return;
    }

    if (e.type === 'tag') {
      convertTag(el as HTMLElement, invertir ? e.before : e.after);
      return;
    }

    if (e.type === 'class') {
      const add = invertir ? e.remove : e.add;
      const remove = invertir ? e.add : e.remove;
      for (const c of remove) el.classList.remove(c);
      for (const c of add) el.classList.add(c);
      return;
    }

    if (e.type === 'style') {
      // El estilo no vive en el elemento sino en una hoja aparte, que se
      // regenera a partir de los cambios pendientes: deshacer la entrada ya
      // cambió esa lista, y el repintado ocurre solo. Lo único que hace falta
      // acá es que el elemento conserve su clase de enganche — es inocua sin
      // ninguna regla que la nombre.
      el.classList.add(overrideClassFor(e.targetId));
      return;
    }

    const valor = invertir ? e.before : e.after;
    if (e.kind === 'rich') {
      el.innerHTML = valor;
    } else {
      const nodo = Array.from(el.childNodes).find(n => n.nodeType === 3 && (n.textContent ?? '').trim());
      if (nodo) nodo.textContent = valor;
    }
    valorActual.current.set(e.targetId, valor);
  }, [getDoc]);

  // Los atajos de teclado llaman a la versión actual, no a la del render en que
  // se registró el listener.
  useEffect(() => { atajos.undo = () => { undoRef.current?.(); }; atajos.redo = () => { redoRef.current?.(); }; }, []);
  const undoRef = useRef<(() => void) | null>(null);
  const redoRef = useRef<(() => void) | null>(null);

  const undo = useCallback((): void => {
    // Se cierra la edición abierta: su último estado ya está registrado, y dejar
    // el contenteditable activo mientras se reescribe el contenido desincroniza
    // el cursor.
    const e = historia.current.undo();
    if (!e) return;
    aplicar(e, true);
    sync();
  }, [aplicar, sync]);

  const redo = useCallback((): void => {
    const e = historia.current.redo();
    if (!e) return;
    aplicar(e, false);
    sync();
  }, [aplicar, sync]);

  /**
   * Revierte un cambio puntual de la lista de pendientes.
   *
   * No es lo mismo que deshacer: deshacer saca el ÚLTIMO paso, esto saca un
   * cambio elegido sin importar cuándo se hizo. Se registra como un paso más, así
   * que también se puede deshacer.
   */
  useEffect(() => { undoRef.current = undo; redoRef.current = redo; });

  const revert = useCallback((cambio: PendingChange): void => {
    if (cambio.type === 'text' && cambio.original !== undefined && cambio.kind) {
      registrarTexto(cambio.targetId, cambio.kind, cambio.original);
      aplicar({ type: 'text', targetId: cambio.targetId, kind: cambio.kind,
                before: cambio.value ?? '', after: cambio.original }, false);
    } else if (cambio.type === 'class') {
      const add = cambio.removed ?? [];
      const remove = cambio.added ?? [];
      historia.current.push({ type: 'class', targetId: cambio.targetId, add, remove }, Date.now());
      aplicar({ type: 'class', targetId: cambio.targetId, add, remove }, false);
    } else if ((cambio.type === 'href' || cambio.type === 'tag') && cambio.original !== undefined) {
      const paso = {
        type: cambio.type, targetId: cambio.targetId,
        before: cambio.value ?? '', after: cambio.original,
      } as HistoryEntry;
      historia.current.push(paso, Date.now());
      aplicar(paso, false);
    } else if (cambio.type === 'structure' && cambio.action) {
      // Se saca el paso de la línea de tiempo y se aplica su inverso sobre el
      // documento. Sacarlo es lo que evita que el guardado vuelva a borrar algo
      // que se acaba de reponer.
      const paso = historia.current.dropStructure(cambio.targetId, cambio.action);
      if (paso) aplicar(paso, true);
    }
    sync();
  }, [registrarTexto, aplicar, sync]);

  const stopEditing = useCallback((): HTMLElement | null => {
    const current = editing.current;
    if (!current) return null;
    const { el } = current;
    el.removeEventListener('input', current.onInput);
    el.ownerDocument.removeEventListener('selectionchange', current.onSelectionChange);
    rangoGuardado.current = null;
    el.removeAttribute('contenteditable');
    // Los hijos se blindaron para que no se borren al escribir; se devuelven
    // como estaban.
    for (const child of Array.from(el.children)) child.removeAttribute('contenteditable');
    el.removeEventListener('keydown', onKeyDown);
    el.removeEventListener('paste', onPaste);
    editing.current = null;
    setCanFormat(false);
    editingElementRef.current = null;
    setEditingElement(null);
    setFormatActions([]);
    return el;
  }, []);

  const commitEdit = useCallback((): void => {
    const current = editing.current;
    if (!current) return;
    const { targetId, kind } = current;
    const el = stopEditing();
    if (!el) return;
    // Se lee lo mismo que el servidor va a escribir: el texto completo si es una
    // hoja, solo el texto propio si además tiene hijos.
    registrarTexto(targetId, kind, editableValueOf(el, kind));
    sync();
  }, [stopEditing, sync, registrarTexto]);

  const beginEdit = useCallback((el: HTMLElement, opts?: { placeCaret?: boolean }): boolean => {
    const editability = classify(el);
    if (editability.kind === 'none') return false;
    const targetId = el.getAttribute('data-evo-id');
    if (!targetId) return false;

    if (editing.current?.el === el) return true;
    // Si se estaba editando otro, se cierra primero.
    if (editing.current) commitEdit();

    const kind = editability.kind;
    // Se recuerda el contenido de partida para poder armar el `before` del
    // primer paso sobre este elemento.
    if (!valorActual.current.has(targetId)) {
      valorActual.current.set(targetId, editableValueOf(el, kind));
    }

    // El cambio se registra MIENTRAS se escribe, no al salir del elemento.
    //
    // Antes solo se registraba al cerrar la edición, y lo único que la cerraba
    // era hacer clic en otro elemento. Con una sola edición el contador quedaba
    // en cero, el botón Guardar deshabilitado, y ese cambio era imposible de
    // guardar hasta tocar otra cosa.
    //
    // El re-render se dispara solo cuando cambia la CANTIDAD de cambios (0→1 al
    // primer carácter, 1→0 si se vuelve al texto original), no en cada tecla: el
    // registro vive en un ref y no necesita a React para estar al día.
    // Se recuerda la selección mientras se edita, para poder restaurarla si el
    // clic en el panel la pierde.
    const onSelectionChange = (): void => {
      const s = el.ownerDocument.defaultView?.getSelection();
      if (!s?.rangeCount) return;
      const r = s.getRangeAt(0);
      if (el.contains(r.commonAncestorContainer)) rangoGuardado.current = r.cloneRange();
    };
    el.ownerDocument.addEventListener('selectionchange', onSelectionChange);

    const onInput = (): void => {
      registrarTexto(targetId, kind, editableValueOf(el, kind));
      sync();
    };
    el.addEventListener('input', onInput);

    // Los hijos (el ícono de un link, el chevron de una pregunta) quedan como
    // bloques no editables: sin esto, escribir encima los borra de la vista y
    // lo que se ve deja de ser lo que se guarda — el servidor nunca los toca.
    if (kind === 'own') {
      for (const child of Array.from(el.children)) {
        child.setAttribute('contenteditable', 'false');
      }
    }

    // En una hoja se permite formato (negrita, salto, viñetas): contenteditable
    // completo. En un elemento con hijos se queda en texto plano, porque ahí solo
    // se toca el nodo de texto y cualquier markup se perdería al guardar.
    el.setAttribute('contenteditable', kind === 'rich' ? 'true' : 'plaintext-only');
    el.addEventListener('keydown', onKeyDown);
    el.addEventListener('paste', onPaste);
    editing.current = { el, targetId, kind, onInput, onSelectionChange };
    setCanFormat(kind === 'rich');
    editingElementRef.current = el;
    setEditingElement(el);
    setFormatActions(
      kind !== 'rich' ? []
      // Las viñetas solo en un párrafo: en un <h2> o un <a> producen una lista
      // adentro de algo que no puede contenerla.
      : el.tagName.toLowerCase() === 'p' ? ['bold', 'lineBreak', 'bullets']
      : ['bold', 'lineBreak'],
    );
    // Cuando la edición arranca desde un clic, el navegador ya puso el cursor
    // donde corresponde: tocarlo lo mandaría al final del bloque, que es lo que
    // hacía imposible escribir en el medio de una frase.
    if (opts?.placeCaret !== false) {
      el.focus();
      const doc = el.ownerDocument;
      const range = doc.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = doc.defaultView?.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }

    return true;
  }, [classify, commitEdit, sync]);

  const discardAll = useCallback((): void => {
    stopEditing();
    const doc = getDoc();
    if (doc) {
      // Se revierte TODO lo pendiente, texto y clases: salen del mismo registro,
      // así que no hay dos listas que puedan quedar desfasadas.
      for (const cambio of historia.current.pending()) {
        const target = doc.querySelector(`[data-evo-id="${cambio.targetId}"]`);
        if (!target) continue;

        if (cambio.type === 'class') {
          for (const c of cambio.added ?? []) target.classList.remove(c);
          for (const c of cambio.removed ?? []) target.classList.add(c);
          continue;
        }

        const original = cambio.original;
        if (original === undefined) continue;
        if (cambio.kind === 'rich') {
          target.innerHTML = original;
        } else {
          // Restaurar solo el nodo de texto: escribir textContent borraría los
          // hijos que esta edición nunca tocó.
          const nodo = Array.from(target.childNodes).find(n => n.nodeType === 3 && (n.textContent ?? '').trim());
          if (nodo) nodo.textContent = original;
        }
      }
    }
    historia.current.clear();
    valorActual.current.clear();
    sync();
  }, [stopEditing, getDoc, sync]);

  const [canFormat, setCanFormat] = useState(false);
  const [editingElement, setEditingElement] = useState<HTMLElement | null>(null);
  const editingElementRef = useRef<HTMLElement | null>(null);
  const [formatActions, setFormatActions] = useState<Array<'bold' | 'lineBreak' | 'bullets'>>([]);
  /**
   * Última selección hecha DENTRO del elemento que se está editando.
   *
   * Los botones de formato viven en el panel, que es otro documento. Aunque se
   * evite que roben el foco, alcanza con que el navegador colapse la selección
   * para que la negrita se aplique sobre nada. Guardarla y restaurarla es la
   * garantía que no depende del comportamiento del foco.
   */
  const rangoGuardado = useRef<Range | null>(null);

  const applyFormat = useCallback((accion: 'bold' | 'lineBreak' | 'bullets'): void => {
    const actual = editing.current;
    if (!actual || actual.kind !== 'rich') return;
    const doc = actual.el.ownerDocument;
    const sel = doc.defaultView?.getSelection();

    // El comando actúa sobre la selección del documento. Si se perdió (el clic en
    // el panel), se restaura la última que hubo adentro del elemento.
    const dentro = sel?.rangeCount
      ? actual.el.contains(sel.getRangeAt(0).commonAncestorContainer)
      : false;
    if (!dentro) {
      actual.el.focus();
      const guardado = rangoGuardado.current;
      if (guardado && sel) { sel.removeAllRanges(); sel.addRange(guardado); }
    }
    switch (accion) {
      // La negrita se aplica a mano y no con execCommand: sobre un texto que YA
      // es negrita (un título), Chrome no envuelve en <strong> sino que mete un
      // <span style="font-weight: normal"> para QUITARLA. Ese span no está en la
      // lista blanca del servidor, así que se veía el cambio en pantalla y al
      // guardar volvía atrás — exactamente lo que no puede pasar.
      case 'bold': toggleBold(doc, actual.el); break;
      case 'lineBreak': doc.execCommand('insertLineBreak'); break;
      // El navegador arma bien la lista (<ul><li>), pero la deja SIN clase, y los
      // templates hacen `list-style: none` en el reset. Resultado: el comando
      // funcionaba y en pantalla no pasaba nada — se veía como un botón roto.
      //
      // La clase la pone también el servidor al reconstruir; ponerla acá es lo
      // que hace que lo que se ve sea lo que se guarda, en vez de tener que
      // guardar para enterarse de si funcionó.
      case 'bullets': {
        doc.execCommand('insertUnorderedList');
        for (const ul of Array.from(actual.el.querySelectorAll('ul'))) {
          ul.classList.add(LIST_UTILITY);
        }
        break;
      }
    }
    // execCommand no dispara 'input' en todos los casos: se registra a mano.
    registrarTexto(actual.targetId, actual.kind, editableValueOf(actual.el, actual.kind));
    sync();
  }, [sync]);

  const clearEdits = useCallback((): void => {
    stopEditing();
    historia.current.clear();
    valorActual.current.clear();
    sync();
  }, [stopEditing, sync]);

  useEffect(() => { commitEditRef.current = commitEdit; });

  return { edits, getEdits, beginEdit, commitEdit, discardAll, clearEdits, classify, applyFormat, canFormat, formatActions, editingElement, editingElementRef,
    setHref, setTag, duplicateBlock, removeBlock, moveBlock, puedeMover,
    undo, redo, canUndo, canRedo, revert, registrarClases, registrarEstilo };
}

/** El <strong> que envuelve a la selección, si está dentro del elemento editado. */
function strongQueEnvuelve(nodo: Node | null, raiz: HTMLElement): HTMLElement | null {
  let n: Node | null = nodo;
  while (n && n !== raiz) {
    if (n.nodeType === 1 && (n as HTMLElement).tagName === 'STRONG') return n as HTMLElement;
    n = n.parentNode;
  }
  return null;
}

/**
 * Pone o saca negrita sobre la selección, produciendo exactamente <strong>.
 *
 * Sin execCommand: el resultado tiene que ser lo que el servidor acepta, no lo
 * que cada navegador decida emitir.
 */
function toggleBold(doc: Document, raiz: HTMLElement): void {
  const sel = doc.defaultView?.getSelection();
  if (!sel?.rangeCount) return;
  const range = sel.getRangeAt(0);

  const yaEsta = strongQueEnvuelve(range.commonAncestorContainer, raiz);
  if (yaEsta) {
    // Sacar la negrita: se desenvuelve el <strong> dejando su contenido donde estaba.
    const padre = yaEsta.parentNode;
    if (!padre) return;
    while (yaEsta.firstChild) padre.insertBefore(yaEsta.firstChild, yaEsta);
    padre.removeChild(yaEsta);
    padre.normalize();
    return;
  }

  if (range.collapsed) return; // sin nada seleccionado no hay qué poner en negrita
  const strong = doc.createElement('strong');
  strong.appendChild(range.extractContents());
  range.insertNode(strong);

  // Dejar seleccionado lo que se acaba de marcar, para poder deshacerlo enseguida.
  const nuevo = doc.createRange();
  nuevo.selectNodeContents(strong);
  sel.removeAllRanges();
  sel.addRange(nuevo);
}

// En una hoja, Enter inserta un salto de línea (<br>), que ahora se guarda. En un
// elemento con hijos sigue cerrando la edición: ahí solo se guarda texto plano y
// el salto se perdería al escribir.
/**
 * Acciones que el handler de teclado necesita.
 *
 * El listener se registra una sola vez por elemento y tiene que ver siempre la
 * versión actual, así que se leen de acá y no del closure.
 */
const atajos: { undo?: () => void; redo?: () => void } = {};

function onKeyDown(event: KeyboardEvent): void {
  const el = event.currentTarget as HTMLElement | null;
  const meta = event.metaKey || event.ctrlKey;

  if (meta && event.key.toLowerCase() === 'b') {
    // Negrita por teclado, con el mismo camino que el botón: <strong>, nunca lo
    // que el navegador decida emitir.
    event.preventDefault();
    if (el) toggleBold(el.ownerDocument, el);
    return;
  }
  if (meta && event.key.toLowerCase() === 'z') {
    // Deshacer dentro de la edición. Es el historial del propio contenteditable;
    // el registro se corrige solo porque undo dispara 'input'.
    event.preventDefault();
    el?.ownerDocument.execCommand(event.shiftKey ? 'redo' : 'undo');
    return;
  }
  if (event.key !== 'Enter') return;
  if (el?.getAttribute('contenteditable') === 'true') {
    // Salto simple, no un párrafo nuevo: <br> está en la lista blanca, <div> no.
    event.preventDefault();
    el.ownerDocument.execCommand('insertLineBreak');
    return;
  }
  event.preventDefault();
  el?.blur();
}

// Pegar mete el HTML de origen (negritas, spans, estilos del portapapeles).
// Como solo se guarda el texto, lo que se vería en pantalla no sería lo que se
// guarda. Se fuerza texto plano.
function onPaste(event: ClipboardEvent): void {
  event.preventDefault();
  const text = event.clipboardData?.getData('text/plain') ?? '';
  const doc = (event.currentTarget as HTMLElement).ownerDocument;
  const sel = doc.defaultView?.getSelection();
  if (!sel?.rangeCount) return;
  sel.deleteFromDocument();
  sel.getRangeAt(0).insertNode(doc.createTextNode(text));
  sel.collapseToEnd();
}
